/* ============================================================
   providerBuildVolumeFilterView — PF-5 (TASK-e15bdfab): pure,
   node-testable view model for the «Влезет X×Y×Z» customer
   filter on the provider catalog page.

   - parseBuildVolumeFilter: raw input strings → numeric filter
     (each axis independent; empty = unconstrained; anything
     non-positive / non-finite is invalid → error, nothing sent).
   - buildVolumeFilterIsActive / buildVolumeFilterLabel: chip
     presentation of the applied filter.
   ============================================================ */

export interface BuildVolumeFilter {
  minBuildX?: number;
  minBuildY?: number;
  minBuildZ?: number;
}

export type BuildVolumeFilterParse =
  | { filter: BuildVolumeFilter; error: null }
  | { filter: BuildVolumeFilter; error: string };

/** Trim + parse one axis: '' → undefined, valid positive number → value. */
function parseAxis(raw: string): number | 'invalid' | undefined {
  const s = raw.trim();
  if (s.length === 0) return undefined;
  const n = Number(s.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return 'invalid';
  return n;
}

export function parseBuildVolumeFilter(x: string, y: string, z: string): BuildVolumeFilterParse {
  const px = parseAxis(x);
  const py = parseAxis(y);
  const pz = parseAxis(z);
  if (px === 'invalid' || py === 'invalid' || pz === 'invalid') {
    return { filter: {}, error: 'Размеры должны быть положительными числами (мм)' };
  }
  return {
    filter: {
      ...(px !== undefined ? { minBuildX: px } : {}),
      ...(py !== undefined ? { minBuildY: py } : {}),
      ...(pz !== undefined ? { minBuildZ: pz } : {}),
    },
    error: null,
  };
}

export function buildVolumeFilterIsActive(filter: BuildVolumeFilter): boolean {
  return filter.minBuildX !== undefined
    || filter.minBuildY !== undefined
    || filter.minBuildZ !== undefined;
}

/** Chip label for the applied filter, e.g. «Влезет 250×250×250». */
export function buildVolumeFilterLabel(filter: BuildVolumeFilter): string | null {
  if (!buildVolumeFilterIsActive(filter)) return null;
  const axis = (v: number | undefined) => (v === undefined ? '·' : String(Math.round(v * 10) / 10));
  return `Влезет ${axis(filter.minBuildX)}×${axis(filter.minBuildY)}×${axis(filter.minBuildZ)} мм`;
}
